import React, { useRef, useState } from "react";
import { Head, useForm, usePage } from "@inertiajs/react";
import AuthenticatedLayout from "@/Layouts/AuthenticatedLayout";
import DangerButton from "@/Components/DangerButton";
import InputError from "@/Components/InputError";
import InputLabel from "@/Components/InputLabel";
import Modal from "@/Components/Modal";
import SecondaryButton from "@/Components/SecondaryButton";
import TextInput from "@/Components/TextInput";

export default function AccountDeletion({ appName, contactEmail }) {
    const { auth } = usePage().props;
    const isAuthenticated = Boolean(auth?.user);

    const [confirmingDeletion, setConfirmingDeletion] = useState(false);
    const passwordInput = useRef();

    const {
        data,
        setData,
        post,
        processing,
        reset,
        errors,
        clearErrors,
    } = useForm({
        password: "",
    });

    const openConfirmModal = () => setConfirmingDeletion(true);

    const submitDeletion = (e) => {
        e.preventDefault();
        post(route("account.deletion.destroy"), {
            preserveScroll: true,
            onSuccess: () => {
                setConfirmingDeletion(false);
            },
            onError: () => passwordInput.current?.focus(),
            onFinish: () => reset(),
        });
    };

    const closeModal = () => {
        setConfirmingDeletion(false);
        clearErrors();
        reset();
    };

    return (
        <AuthenticatedLayout>
            <Head title="Account Deletion Policy" />
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="bg-white dark:bg-boxdark rounded-lg shadow-lg p-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                        Account Deletion Policy - {appName}
                    </h1>

                    <div className="prose prose-lg max-w-none dark:prose-invert">
                        <p className="text-gray-700 dark:text-gray-300 mb-6">
                            This page explains how you can request the deletion
                            of your account and associated data from {appName}.
                        </p>

                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                            How to Request Account Deletion
                        </h2>

                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6">
                            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
                                How to Request Account Deletion:
                            </h3>
                            <div className="space-y-4 text-blue-800 dark:text-blue-200">
                                <p className="text-lg font-medium">
                                    Send an email to{" "}
                                    <a
                                        href={`mailto:${contactEmail}?subject=Account Deletion Request&body=Please delete my account and all associated data. My account details are:%0D%0A%0D%0AUsername/Email: [Your username or email]%0D%0A%0D%0AThank you.`}
                                        className="text-blue-600 dark:text-blue-400 underline font-semibold"
                                    >
                                        {contactEmail}
                                    </a>
                                </p>
                                <p className="text-sm">
                                    Include your username or email address in
                                    your request. We will process your deletion
                                    request within 24-48 hours.
                                </p>
                            </div>
                        </div>

                        {isAuthenticated && (
                            <div className="bg-stone-50 dark:bg-stone-900/30 border-2 border-stone-300 dark:border-stone-600 rounded-lg p-6 mb-8">
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                                    Delete your account from this device
                                </h2>
                                <p className="text-gray-700 dark:text-gray-300 mb-4 text-sm leading-relaxed">
                                    This will permanently remove your personal
                                    information from our systems while keeping an
                                    anonymized user record so existing posts and
                                    references stay valid. You will be signed out
                                    immediately. This cannot be undone.
                                </p>
                                <DangerButton type="button" onClick={openConfirmModal}>
                                    Delete my account
                                </DangerButton>

                                <Modal show={confirmingDeletion} onClose={closeModal}>
                                    <form
                                        onSubmit={submitDeletion}
                                        className="p-6"
                                    >
                                        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                                            Confirm account deletion
                                        </h2>
                                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                            Enter your current password to
                                            confirm. Your profile data will be
                                            cleared and your session ended.
                                        </p>

                                        <div className="mt-6">
                                            <InputLabel
                                                htmlFor="account-deletion-password"
                                                value="Password"
                                                className="sr-only"
                                            />
                                            <TextInput
                                                id="account-deletion-password"
                                                type="password"
                                                name="password"
                                                ref={passwordInput}
                                                value={data.password}
                                                onChange={(e) =>
                                                    setData(
                                                        "password",
                                                        e.target.value,
                                                    )
                                                }
                                                className="mt-1 block w-full max-w-md"
                                                isFocused
                                                placeholder="Password"
                                                autoComplete="current-password"
                                            />
                                            <InputError
                                                message={errors.password}
                                                className="mt-2"
                                            />
                                        </div>

                                        <div className="mt-6 flex justify-end gap-3">
                                            <SecondaryButton
                                                type="button"
                                                onClick={closeModal}
                                            >
                                                Cancel
                                            </SecondaryButton>
                                            <DangerButton
                                                type="submit"
                                                disabled={processing}
                                            >
                                                {processing
                                                    ? "Working…"
                                                    : "Delete my account"}
                                            </DangerButton>
                                        </div>
                                    </form>
                                </Modal>
                            </div>
                        )}

                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                            Data Deletion Information
                        </h2>

                        <div className="grid md:grid-cols-2 gap-6 mb-6">
                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-3">
                                    Data That Will Be Deleted:
                                </h3>
                                <ul className="list-disc list-inside space-y-1 text-red-800 dark:text-red-200">
                                    <li>Your personal profile information</li>
                                    <li>
                                        All posts and content you've created
                                    </li>
                                    <li>Comments and interactions</li>
                                    <li>Private messages and conversations</li>
                                    <li>Account preferences and settings</li>
                                    <li>Uploaded images and media files</li>
                                    <li>Following/follower relationships</li>
                                    <li>Notification preferences</li>
                                </ul>
                            </div>

                            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-3">
                                    Data That May Be Retained:
                                </h3>
                                <ul className="list-disc list-inside space-y-1 text-yellow-800 dark:text-yellow-200">
                                    <li>
                                        Anonymized analytics data (no personal
                                        identifiers)
                                    </li>
                                    <li>
                                        Legal compliance records (if required by
                                        law)
                                    </li>
                                    <li>
                                        Backup data for up to 30 days (for
                                        security purposes)
                                    </li>
                                    <li>
                                        Transaction records (for financial
                                        compliance)
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                            Data Retention Period
                        </h2>

                        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
                            <p className="text-gray-700 dark:text-gray-300 mb-4">
                                Once you request account deletion, the following
                                timeline applies:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                                <li>
                                    <strong>Immediate:</strong> Your account
                                    will be deactivated and you won't be able to
                                    log in
                                </li>
                                <li>
                                    <strong>Within 24 hours:</strong> Your
                                    personal data will be removed from active
                                    systems
                                </li>
                                <li>
                                    <strong>Within 30 days:</strong> All backup
                                    copies of your data will be permanently
                                    deleted
                                </li>
                                <li>
                                    <strong>Legal records:</strong> May be
                                    retained longer if required by applicable
                                    laws
                                </li>
                            </ul>
                        </div>

                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                            Important Notes
                        </h2>

                        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-6 mb-6">
                            <ul className="list-disc list-inside space-y-2 text-orange-800 dark:text-orange-200">
                                <li>
                                    <strong>Irreversible:</strong> Account
                                    deletion is permanent and cannot be undone
                                </li>
                                <li>
                                    <strong>Data Recovery:</strong> Once
                                    deleted, your data cannot be recovered
                                </li>
                                <li>
                                    <strong>Third-party Services:</strong> If
                                    you've connected third-party services, you
                                    may need to revoke access separately
                                </li>
                                <li>
                                    <strong>Legal Obligations:</strong> Some
                                    data may be retained if required by law or
                                    legal proceedings
                                </li>
                                <li>
                                    <strong>Backup Systems:</strong> Complete
                                    deletion from all backup systems may take up
                                    to 30 days
                                </li>
                            </ul>
                        </div>

                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                            Contact Information
                        </h2>

                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
                            <p className="text-green-800 dark:text-green-200 mb-4">
                                If you have any questions about account deletion
                                or need assistance with the process, please
                                contact us:
                            </p>
                            <div className="space-y-2 text-green-700 dark:text-green-300">
                                <p>
                                    <strong>Email:</strong>{" "}
                                    <a
                                        href={`mailto:${contactEmail}`}
                                        className="text-green-600 dark:text-green-400 underline"
                                    >
                                        {contactEmail}
                                    </a>
                                </p>
                                <p>
                                    <strong>Response Time:</strong> We typically
                                    respond within 24-48 hours
                                </p>
                                <p>
                                    <strong>Support Hours:</strong> Monday -
                                    Friday, 9:00 AM - 5:00 PM (your local
                                    timezone)
                                </p>
                            </div>
                        </div>

                        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Last updated: {new Date().toLocaleDateString()}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </AuthenticatedLayout>
    );
}
